/**
 * RequestCommentModal Component
 *
 * A simple modal that allows users to optionally add a comment/reason
 * before completing an action (approve/reject).
 *
 * Both Skip and Send approve the invite — Skip sends no comment,
 * Send includes the typed comment.
 *
 * @see Figma designs: node-id=1813-92889
 */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LytModal from '../ui-core/modal/LytModal';
import LytButton, { BUTTON_STYLES } from '../library/button/LytButton';
import LytTextArea from '../library/text-area/LytTextArea';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
const TextAreaWrapper = styled.div`
    width: 100%;

    textarea {
        width: 100%;
    }
`;
const FooterActions = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
`;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * RequestCommentModal - Modal for adding an optional comment before action
 *
 * Both "Skip" and "Send" approve the invite. The only difference is whether
 * a comment is attached. This consolidates both paths into a single handler.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 */
const RequestCommentModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
}) => {
    const [comment, setComment] = useState('');

    // Handle close action — memoized to keep a stable reference so
    // LytModal's focus-management effect doesn't re-fire on every render.
    const handleClose = useCallback(() => {
        closeModal
            ? closeModal({
                  dismissed: true,
              })
            : onClose?.();
    }, [closeModal, onClose]);
    if (!open) return null;

    /**
     * Resolve the modal — used by both Skip and Send.
     * @param {boolean} includeComment - Whether to attach the typed comment
     */
    const handleResolve = (includeComment) => {
        const trimmed = comment.trim() || null;
        closeModal?.({
            submitted: true,
            comment: includeComment ? trimmed : null,
        });
    };

    // Modal footer actions
    const footerActions = (
        <FooterActions>
            <LytButton
                type={BUTTON_STYLES.invisible}
                onClick={() => handleResolve(false)}>
                Skip
            </LytButton>
            <LytButton
                type={BUTTON_STYLES.secondary}
                onClick={() => handleResolve(true)}>
                Send
            </LytButton>
        </FooterActions>
    );
    return (
        <LytModal
            open={open}
            title="Optional: Add a Comment"
            onClose={handleClose}
            actions={footerActions}
            size="small">
            <ModalContent>
                <TextAreaWrapper>
                    <LytTextArea
                        name="comment"
                        label="Comment"
                        placeholder="explain your reasoning (optional)"
                        initialValue={comment}
                        onChange={setComment}
                        rows={4}
                    />
                </TextAreaWrapper>
            </ModalContent>
        </LytModal>
    );
};
RequestCommentModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
};
export default RequestCommentModal;
